import { memo } from "react"
import { makeStyles } from "tss-react/mui"
import { faSpinnerThird } from "@fortawesome/pro-light-svg-icons/faSpinnerThird"
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons"
import { faLock } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Checkbox, IconButton, lighten, Stack, Tooltip, Typography } from "@mui/material"

import { ResourceDetailsDialogParams } from "../../../../dialogs/resourceDetailsDialog/ResourceDetailsDialog"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"

import { TimelineResourceLabelContent } from "../calendar/fullCalendarTypes"
import { SearchableText } from "../../../../search/Searchable"

const useStyles = makeStyles()((theme) => ({
    checkbox: {
        padding: ".25rem",
    },
    dimmed: {
        color: theme.palette.tailwind.slate[400],
    },
    resourceLabelText: {
        display: "block",
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
    },
    paddingLeft: {
        paddingLeft: ".5rem",
    },
    buttonWrap: {
        paddingLeft: "1rem",
        cursor: "auto",
    },
    iconButton: {
        padding: "0",
    },
    icon: {
        color: lighten(theme.palette.tailwind.slate[400], 0.4),
    },
    rowBusy: {
        cursor: "wait",
    },
    checkboxSlot: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        width: "1.75rem",
        height: "1.75rem",
    },
    selectionSpinner: {
        fontSize: "1.125rem",
        color: theme.palette.primary.main,
    },
}))

type ResourceCalendarLabelContentProps = Readonly<{
    resource: TimelineResourceLabelContent["resource"]
    searchRegex: RegExp | undefined
    isChecked?: boolean
    toggleResourceSelection?: (resourceId: string) => void | Promise<void>
    isLocked?: boolean
    handleOpenDetails?: (res: ResourceDetailsDialogParams) => void
    isEventsLoading?: boolean
    isSelectionPending?: boolean
    eventsLoadingNativeTitle?: string
}>

export const ResourceCalendarLabelContent = memo(function ResourceCalendarLabelContent({
    isChecked,
    resource,
    searchRegex,
    toggleResourceSelection,
    isLocked = false,
    handleOpenDetails,
    isEventsLoading = false,
    isSelectionPending = false,
    eventsLoadingNativeTitle,
}: ResourceCalendarLabelContentProps) {
    const { classes, cx } = useStyles()
    const { extendedProps, id, title } = resource

    const isDimmed = !!extendedProps.isDimmed

    const handlePreventClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation()
    }

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        handleOpenDetails && handleOpenDetails({ title, ...extendedProps })
    }

    const handleRowClick = () => {
        if (isLocked || !toggleResourceSelection) return
        void toggleResourceSelection(id)
    }

    const showRowWaitCursor = isEventsLoading || (!!toggleResourceSelection && isSelectionPending)

    const labelRow = (
        <Stack
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="nowrap"
            gap="1rem"
            onClick={handleRowClick}
            title={isEventsLoading ? eventsLoadingNativeTitle : ""}
            aria-busy={isSelectionPending || undefined}
            className={cx(showRowWaitCursor && classes.rowBusy)}
        >
            <Stack flexDirection="row" alignItems="center" minWidth={0}>
                {!isLocked && toggleResourceSelection && (
                    <span className={classes.checkboxSlot}>
                        {isSelectionPending ? (
                            <FontAwesomeIcon
                                icon={faSpinnerThird}
                                spin
                                className={classes.selectionSpinner}
                                aria-hidden
                            />
                        ) : (
                            <Checkbox checked={isChecked} size="small" className={classes.checkbox} />
                        )}
                    </span>
                )}
                <SearchableText search={searchRegex}>
                    <Typography
                        variant="body1"
                        className={cx(classes.resourceLabelText, {
                            [classes.dimmed]: isDimmed,
                            [classes.paddingLeft]: !toggleResourceSelection || isLocked,
                        })}
                    >
                        {title}
                    </Typography>
                </SearchableText>
            </Stack>
            <Stack
                flexDirection="row"
                alignItems="center"
                gap=".75rem"
                className={classes.buttonWrap}
                onClick={handlePreventClick}
            >
                {extendedProps?.accessControl?.hasAccess === false && (
                    <LocalizedStrict id="create-order-tooltip-resource-lock-button" attrs={{ title: true }}>
                        <Tooltip title="Can only be booked by selected users">
                            <FontAwesomeIcon icon={faLock} className={classes.icon} />
                        </Tooltip>
                    </LocalizedStrict>
                )}
                <LocalizedStrict id="create-order-tooltip-resource-info-button" attrs={{ title: true }}>
                    <Tooltip title="Show resource details">
                        <IconButton onClick={handleClick} className={classes.iconButton} size="small">
                            <FontAwesomeIcon icon={faCircleInfo} className={classes.icon} />
                        </IconButton>
                    </Tooltip>
                </LocalizedStrict>
            </Stack>
        </Stack>
    )

    return labelRow
})
